import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/store";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { CreditCard, ShieldCheck, QrCode } from "@phosphor-icons/react";
import { iconFor, tintFor } from "@/lib/categoryIcons";

export default function Checkout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, storeId, subtotal, clear } = useCart();
  const [store, setStore] = useState(null);
  const [address, setAddress] = useState(user?.address || "");
  const [method, setMethod] = useState("stripe"); // 'stripe' | 'snapscan' | 'zapper'
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!storeId || items.length === 0) {
      navigate("/cart");
      return;
    }
    api.get(`/stores/${storeId}`).then((r) => setStore(r.data.store));
  }, [storeId, items.length, navigate]);

  if (!store) return <div className="text-[var(--text-2)]">Loading…</div>;

  const sub = subtotal();
  const fee = store.delivery_fee;
  const tax = +(sub * 0.15).toFixed(2);
  const total = +(sub + fee + tax).toFixed(2);

  const pay = async () => {
    if (!address.trim()) {
      toast.error("Please enter a delivery address");
      return;
    }
    setLoading(true);
    try {
      const jitter = () => (Math.random() - 0.5) * 0.02;
      const payload = {
        store_id: storeId,
        items: items.map(({ product_id, quantity }) => ({ product_id, quantity })),
        delivery_address: address,
        delivery_lat: store.lat + jitter(),
        delivery_lng: store.lng + jitter(),
        origin_url: window.location.origin,
      };
      if (method === "stripe") {
        const { data } = await api.post("/orders/checkout", payload);
        clear();
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post("/orders/qr-checkout", {
          ...payload,
          method,
        });
        clear();
        window.location.href = `/pay/qr/${data.order_id}`;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="space-y-6">
        <div>
          <span className="label-eyebrow">Step 2 · Deliver to</span>
          <h1 className="mt-1 font-display text-4xl">Delivery details</h1>
        </div>
        <div className="card-panel p-6 space-y-4">
          <div>
            <label className="label-eyebrow">Address</label>
            <Input
              data-testid={TID.addressInput}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="72 Kloof Street, Gardens, Cape Town"
              className="mt-2 bg-white border-2 border-[var(--text)] h-12 rounded-full text-[var(--text)]"
              style={{ boxShadow: "3px 3px 0 0 var(--text)" }}
            />
          </div>
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--text-2)] flex items-center gap-2 pt-2 border-t-2 border-[var(--text)]/10">
            <ShieldCheck size={14} weight="fill" className="text-[var(--green)]" />
            Contactless delivery available at checkout
          </div>
        </div>
        <div className="card-panel p-6 space-y-3">
          <span className="label-eyebrow">Payment method</span>
          {[
            { key: "stripe", label: "Card · Apple Pay · Google Pay", sub: "Secure via Stripe", icon: CreditCard, colour: "var(--blue)" },
            { key: "snapscan", label: "SnapScan", sub: "Scan the QR with the SnapScan app", icon: QrCode, colour: "var(--red)" },
            { key: "zapper", label: "Zapper", sub: "Scan the QR with the Zapper app", icon: QrCode, colour: "var(--green)" },
          ].map((opt) => {
            const active = method === opt.key;
            return (
              <button
                key={opt.key}
                data-testid={`pay-method-${opt.key}`}
                onClick={() => setMethod(opt.key)}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 border-[var(--text)] text-left transition ${active ? "" : "bg-white hover:bg-[var(--yellow)]/40"}`}
                style={active ? { background: opt.colour, color: "#fff", boxShadow: "3px 3px 0 0 var(--text)" } : {}}
              >
                <opt.icon size={26} weight="regular" />
                <div className="flex-1">
                  <p className="font-black">{opt.label}</p>
                  <p className={`text-[11px] font-bold uppercase tracking-widest ${active ? "text-white/85" : "text-[var(--text-2)]"}`}>{opt.sub}</p>
                </div>
                <span className={`w-5 h-5 rounded-full border-2 border-[var(--text)] ${active ? "bg-white" : ""}`} />
              </button>
            );
          })}
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--text-3)] pt-1">
            SnapScan &amp; Zapper are demo-only until merchant IDs are wired
          </p>
        </div>
      </div>

      <div>
        <div className="card-panel p-6 sticky top-28">
          <h3 className="font-display text-2xl mb-4">{store.name}</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {items.map((it) => {
              const { icon: Icon, colour } = iconFor(it.category);
              return (
              <div key={it.product_id} className="flex items-center gap-3 text-sm">
                <div
                  className="w-10 h-10 rounded-lg flex-shrink-0 border-2 border-[var(--text)] flex items-center justify-center"
                  style={tintFor(it.category)}
                >
                  <Icon size={20} weight="regular" color={colour} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate font-bold">{it.name}</p>
                  <p className="text-xs text-[var(--text-2)]">R{it.price.toFixed(2)} × {it.quantity}</p>
                </div>
                <span className="font-bold">R{(it.price * it.quantity).toFixed(2)}</span>
              </div>
              );
            })}
          </div>
          <div className="mt-6 pt-4 border-t-2 border-[var(--text)] space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[var(--text-2)]">Subtotal</span><span>R{sub.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-2)]">Delivery</span><span>R{fee.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-2)]">VAT</span><span>R{tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-display text-3xl mt-3 pt-3 border-t-2 border-[var(--text)]"><span>Total</span><span>R{total.toFixed(2)}</span></div>
          </div>
          <button
            data-testid={TID.checkoutPayBtn}
            disabled={loading}
            onClick={pay}
            className="btn-primary w-full mt-6"
          >
            {loading
              ? (method === "stripe" ? "Redirecting to Stripe…" : "Generating QR…")
              : method === "stripe"
              ? `Pay R${total.toFixed(2)}`
              : `Continue with ${method === "snapscan" ? "SnapScan" : "Zapper"} · R${total.toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
